import React, { useEffect, useState } from "react";
import { Clock, Truck } from "lucide-react";
import { getOrderCycle } from "@/lib/orderCycle";

/**
 * Honest urgency: live countdown to Thursday 11pm Australia/Perth.
 *
 * Two states, swapped automatically as the clock crosses Thursday 23:00:
 *  - BEFORE (Sun 00:00 → Thu 23:00 Perth): countdown to cutoff, "Order in the
 *    next Xd Xh Xm for Saturday delivery".
 *  - AFTER  (Thu 23:00 → Sat 23:59 Perth): first-in-line messaging, "Ordering
 *    now? You're first in line for next Saturday, <date>."
 *
 * All time logic lives in `lib/orderCycle.js` so the announcement bar can
 * consume the same source of truth.
 */
export default function CutoffCountdown({ variant = "light", className = "" }) {
  const [cycle, setCycle] = useState(() => getOrderCycle());

  useEffect(() => {
    const id = setInterval(() => setCycle(getOrderCycle()), 1000);
    return () => clearInterval(id);
  }, []);

  const isDark = variant === "dark";
  const boxCls = isDark
    ? "bg-white/95 text-black border border-black/10"
    : "bg-black/60 text-white backdrop-blur border border-white/20";
  const iconCls = isDark ? "text-[color:var(--tbb-burgundy)]" : "text-white/80";
  const digitCls = isDark ? "text-[color:var(--tbb-burgundy)]" : "text-white";
  const labelCls = isDark ? "text-[color:var(--tbb-muted)]" : "text-white/70";

  // ── Post-cutoff state ─────────────────────────────────────────────────
  if (cycle.mode === "post-cutoff") {
    return (
      <div
        className={`inline-flex items-center gap-3 px-4 py-2.5 ${boxCls} ${className}`}
        data-testid="cutoff-countdown"
        data-mode="post-cutoff"
        role="status"
        aria-live="polite"
      >
        <Truck size={14} className={iconCls} />
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold leading-none mb-1">
            Missed this week&apos;s cutoff
          </div>
          <div className="text-sm font-medium leading-snug" data-testid="cutoff-post-message">
            Ordering now? You&apos;re first in line for next <b>{cycle.deliveryDateLabel}</b>.
          </div>
        </div>
      </div>
    );
  }

  // ── Countdown state ───────────────────────────────────────────────────
  const { days, hours, mins, secs } = cycle.remaining;
  return (
    <div
      className={`inline-flex items-center gap-3 px-4 py-2.5 ${boxCls} ${className}`}
      data-testid="cutoff-countdown"
      data-mode="countdown"
      role="timer"
      aria-live="polite"
      aria-label={`Order in the next ${days} days, ${hours} hours, ${mins} minutes for Saturday delivery.`}
    >
      <Clock size={14} className={iconCls} />
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold leading-none mb-1">
          Order in the next {days > 0 ? `${days}d ` : ""}{hours}h {mins}m for Saturday delivery
        </div>
        <div className="flex items-baseline gap-2 font-mono tabular-nums" data-testid="cutoff-digits">
          {days > 0 && (
            <span>
              <b className={`${digitCls} text-lg`}>{days}</b>
              <span className={`${labelCls} text-[10px] ml-0.5`}>d</span>
            </span>
          )}
          <span>
            <b className={`${digitCls} text-lg`}>{String(hours).padStart(2, "0")}</b>
            <span className={`${labelCls} text-[10px] ml-0.5`}>h</span>
          </span>
          <span>
            <b className={`${digitCls} text-lg`}>{String(mins).padStart(2, "0")}</b>
            <span className={`${labelCls} text-[10px] ml-0.5`}>m</span>
          </span>
          {days === 0 && (
            <span>
              <b className={`${digitCls} text-lg`}>{String(secs).padStart(2, "0")}</b>
              <span className={`${labelCls} text-[10px] ml-0.5`}>s</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
